import React from "react"
import { Tag } from "antd"
import { AppleOutlined, AndroidOutlined, LaptopOutlined } from "@ant-design/icons"

export function getAppUpdatedColor(appUpdated: string): string {
  switch (appUpdated) {
    case "clubhouse":
      return "magenta"
    case "swimminglyIos":
    case "swimminglyfanIos":
      return "blue"
    case "swimminglyAndroid":
    case "swimminglyfanAndroid":
      return "green"
    default:
      return "default"
  }
}

export function getAppUpdatedLabel(appUpdated: string): string {
  switch (appUpdated) {
    case "clubhouse":
      return "Clubhouse"
    case "swimminglyIos":
      return "Swimmingly"
    case "swimminglyfanIos":
      return "SwimminglyFan"
    case "swimminglyAndroid":
      return "Swimmingly Timer"
    case "swimminglyfanAndroid":
      return "SwimminglyFan"
    default:
      return appUpdated || "Unknown App"
  }
}

export function getAppUpdatedIcon(appUpdated: string): React.ReactNode {
  switch (appUpdated) {
    case "clubhouse":
      return <LaptopOutlined />
    case "swimminglyIos":
    case "swimminglyfanIos":
      return <AppleOutlined />
    case "swimminglyAndroid":
    case "swimminglyfanAndroid":
      return <AndroidOutlined />
    default:
      return null
  }
}

interface AppUpdatedTagProps {
  appUpdated: string | null | undefined
}

export default function AppUpdatedTag({ appUpdated }: AppUpdatedTagProps): JSX.Element | null {
  if (!appUpdated) return null

  const color = getAppUpdatedColor(appUpdated)
  const label = getAppUpdatedLabel(appUpdated)
  const icon = getAppUpdatedIcon(appUpdated)

  return (
    <Tag color={color} style={{ display: "inline-flex", alignItems: "center", gap: 4, width: "fit-content" }}>
      {icon} {label}
    </Tag>
  )
}

interface AppUpdatedTagListProps {
  appsUpdated: string[] | null | undefined
  /** Fallback single value used when appsUpdated is empty/null (backward compat) */
  appUpdated?: string | null
  gap?: number
}

export function AppUpdatedTagList({ appsUpdated, appUpdated, gap = 4 }: AppUpdatedTagListProps): JSX.Element | null {
  const apps =
    Array.isArray(appsUpdated) && appsUpdated.length > 0
      ? appsUpdated
      : appUpdated
        ? [appUpdated]
        : []

  if (apps.length === 0) return null

  return (
    <span style={{ display: "inline-flex", flexWrap: "wrap", gap }}>
      {apps.map((app) => (
        <AppUpdatedTag key={app} appUpdated={app} />
      ))}
    </span>
  )
}
